import React, { useState } from 'react';
import { Clock, BarChart3, LogOut, Home, CreditCard, FileText, Settings, ClipboardCheck, Menu, X, User, Users, MapPin, DollarSign, Shield, Briefcase, User as UserCircle, Navigation as NavIcon, Package, Cpu, ChevronDown, FolderOpen, Receipt, Calendar, Route, PackagePlus, Link2, Download } from 'lucide-react';
import { UserRole } from '../hooks/useAuth';

interface NavigationProps {
  currentView: string;
  onViewChange: (view: string) => void;
  onLogout: () => void;
  user?: { email?: string } | null;
  userRole?: UserRole | null;
}

export const Navigation: React.FC<NavigationProps> = ({
  currentView,
  onViewChange,
  onLogout,
  user,
  userRole
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isRegisterDropdownOpen, setIsRegisterDropdownOpen] = useState(false);
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);

  const isAdmin = userRole?.role === 'admin';
  const isEmployee = userRole?.role === 'employee';
  const isExternalPartner = userRole?.role === 'external_partner';

  const allNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, roles: ['admin', 'employee', 'external_partner'], hideOnMobile: false },
    { id: 'register', label: 'Registrera Tid', icon: Clock, roles: ['admin', 'employee', 'external_partner'], hideOnMobile: false },
    { id: 'travel-journal', label: 'Körjournal', icon: NavIcon, roles: ['admin', 'employee'], hideOnMobile: true },
    { id: 'settings', label: 'Inställningar', icon: Settings, roles: ['admin', 'employee', 'external_partner'], hideOnMobile: true },
  ];

  const registerMenuItems = [
    { id: 'customers', label: 'Kunder', icon: Users, roles: ['admin', 'employee'] },
    { id: 'assignments', label: 'Uppdrag', icon: FileText, roles: ['admin', 'employee'] },
    { id: 'articles', label: 'Artikelregister', icon: Package, roles: ['admin', 'employee'] },
    { id: 'packages', label: 'Artikelpaket', icon: PackagePlus, roles: ['admin', 'employee'] },
    { id: 'hardware', label: 'Hårdvaruregister', icon: Cpu, roles: ['admin', 'employee'] },
    { id: 'hardware-linking', label: 'Hårdvarukoppling', icon: Link2, roles: ['admin', 'employee'] },
  ];

  const reportsMenuItems = [
    { id: 'reports', label: 'Kundrapporter', icon: FileText, roles: ['admin', 'employee'] },
    { id: 'distance-report', label: 'Avståndsrapport', icon: Route, roles: ['admin', 'employee'] },
    { id: 'completion-report', label: 'Slutrapport', icon: ClipboardCheck, roles: ['admin', 'employee'] },
    { id: 'monthly-report', label: 'Månadsrapport', icon: Calendar, roles: ['admin', 'employee'] },
    { id: 'unbilled', label: 'Ofakturerat', icon: Receipt, roles: ['admin', 'employee'] },
    { id: 'assignment-export', label: 'Exportera uppdrag', icon: Download, roles: ['admin', 'employee'] },
    { id: 'timebank-purchase-export', label: 'Exportera tidbankskoep', icon: Package, roles: ['admin', 'employee'] },
  ];

  const filteredRegisterItems = registerMenuItems.filter(item =>
    item.roles.includes(userRole?.role || 'external_partner')
  );

  const filteredReportsItems = reportsMenuItems.filter(item =>
    item.roles.includes(userRole?.role || 'external_partner')
  );

  const navItems = allNavItems.filter(item =>
    item.roles.includes(userRole?.role || 'external_partner')
  );

  const getRoleIcon = () => {
    switch (userRole?.role) {
      case 'admin':
        return <Shield className="h-4 w-4" />;
      case 'employee':
        return <Briefcase className="h-4 w-4" />;
      case 'external_partner':
        return <User className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleLabel = () => {
    switch (userRole?.role) {
      case 'admin':
        return 'Admin';
      case 'employee':
        return 'Anställd';
      case 'external_partner':
        return 'Extern Partner';
      default:
        return 'Användare';
    }
  };

  const getRoleBadgeColor = () => {
    switch (userRole?.role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'employee':
        return 'bg-blue-100 text-blue-800';
      case 'external_partner':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleNavigation = (viewId: string) => {
    onViewChange(viewId);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    onLogout();
    setIsMobileMenuOpen(false);
  };

  const getCurrentViewLabel = () => {
    const currentItem = navItems.find(item => item.id === currentView);
    if (currentItem) return currentItem.label;

    const registerItem = registerMenuItems.find(item => item.id === currentView);
    if (registerItem) return registerItem.label;

    const reportsItem = reportsMenuItems.find(item => item.id === currentView);
    if (reportsItem) return reportsItem.label;

    return 'Dashboard';
  };

  const getCurrentViewIcon = () => {
    const currentItem = navItems.find(item => item.id === currentView);
    if (currentItem) return currentItem.icon;

    const registerItem = registerMenuItems.find(item => item.id === currentView);
    if (registerItem) return registerItem.icon;

    const reportsItem = reportsMenuItems.find(item => item.id === currentView);
    if (reportsItem) return reportsItem.icon;

    return Home;
  };

  return (
    <>
      <nav className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center flex-1">
              <div className="flex-shrink-0 flex items-center">
                <Clock className="h-8 w-8 text-blue-600" />
                <span className="ml-2 text-xl font-bold text-gray-900 hidden sm:block">TimBank</span>
              </div>
              {/* Mobile current section indicator */}
              <div className="sm:hidden flex items-center ml-3 pl-3 border-l border-gray-300">
                {React.createElement(getCurrentViewIcon(), { className: "h-5 w-5 text-blue-600 mr-2" })}
                <span className="text-sm font-medium text-gray-900">{getCurrentViewLabel()}</span>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navItems.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <React.Fragment key={item.id}>
                      <button
                        onClick={() => onViewChange(item.id)}
                        className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors ${
                          currentView === item.id
                            ? 'border-blue-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        {item.label}
                      </button>

                      {/* Insert Register dropdown after Dashboard */}
                      {item.id === 'dashboard' && filteredRegisterItems.length > 0 && (
                        <div className="relative">
                          <button
                            onClick={() => setIsRegisterDropdownOpen(!isRegisterDropdownOpen)}
                            onBlur={() => setTimeout(() => setIsRegisterDropdownOpen(false), 200)}
                            className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors ${
                              ['customers', 'assignments', 'articles', 'packages', 'hardware', 'hardware-linking'].includes(currentView)
                                ? 'border-blue-500 text-gray-900'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            <FolderOpen className="h-4 w-4 mr-2" />
                            Register
                            <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${isRegisterDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>

                          {isRegisterDropdownOpen && (
                            <div className="absolute left-0 top-full mt-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[60]">
                              <div className="py-1" role="menu">
                                {filteredRegisterItems.map((regItem) => {
                                  const RegIcon = regItem.icon;
                                  return (
                                    <button
                                      key={regItem.id}
                                      onClick={() => {
                                        onViewChange(regItem.id);
                                        setIsRegisterDropdownOpen(false);
                                      }}
                                      className={`w-full flex items-center px-4 py-2 text-sm transition-colors ${
                                        currentView === regItem.id
                                          ? 'bg-blue-50 text-blue-700'
                                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                      }`}
                                      role="menuitem"
                                    >
                                      <RegIcon className="h-4 w-4 mr-3" />
                                      {regItem.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Insert Reports dropdown after Körjournal */}
                      {item.id === 'travel-journal' && filteredReportsItems.length > 0 && (
                        <div className="relative">
                          <button
                            onClick={() => setIsReportsDropdownOpen(!isReportsDropdownOpen)}
                            onBlur={() => setTimeout(() => setIsReportsDropdownOpen(false), 200)}
                            className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors ${
                              ['reports', 'distance-report', 'completion-report', 'monthly-report', 'unbilled', 'assignment-export', 'timebank-purchase-export'].includes(currentView)
                                ? 'border-blue-500 text-gray-900'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            <BarChart3 className="h-4 w-4 mr-2" />
                            Rapporter
                            <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${isReportsDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>

                          {isReportsDropdownOpen && (
                            <div className="absolute left-0 top-full mt-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[60]">
                              <div className="py-1" role="menu">
                                {filteredReportsItems.map((repItem) => {
                                  const RepIcon = repItem.icon;
                                  return (
                                    <button
                                      key={repItem.id}
                                      onClick={() => {
                                        onViewChange(repItem.id);
                                        setIsReportsDropdownOpen(false);
                                      }}
                                      className={`w-full flex items-center px-4 py-2 text-sm transition-colors ${
                                        currentView === repItem.id
                                          ? 'bg-blue-50 text-blue-700'
                                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                                      }`}
                                      role="menuitem"
                                    >
                                      <RepIcon className="h-4 w-4 mr-3" />
                                      {repItem.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
            <div className="hidden sm:flex items-center space-x-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor()}`}>
                <span className="mr-1">{getRoleIcon()}</span>
                {getRoleLabel()}
              </span>
              <div className="text-sm text-gray-600">
                <span className="font-medium">{user?.email}</span>
              </div>
              <button
                onClick={onLogout}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logga ut
              </button>
            </div>
            <div className="sm:hidden flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors"
                aria-expanded={isMobileMenuOpen}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40 sm:hidden transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile menu slide-in panel */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out sm:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Mobile menu header */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-blue-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">TimBank</span>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-white transition-colors"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* User info section */}
          <div className="px-4 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <User className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor()}`}>
                    <span className="mr-1">{getRoleIcon()}</span>
                    {getRoleLabel()}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation items */}
          <div className="flex-1 overflow-y-auto py-4">
            <nav className="space-y-1 px-2">
              {navItems.filter(item => !item.hideOnMobile).map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className={`w-full flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 shadow-sm border border-blue-100'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className={`h-5 w-5 mr-3 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                    {item.label}
                  </button>
                );
              })}

              {/* Register section in mobile menu */}
              {filteredRegisterItems.length > 0 && (
                <div className="pt-2">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Register
                  </div>
                  {filteredRegisterItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentView === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavigation(item.id)}
                        className={`w-full flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-blue-50 text-blue-700 shadow-sm border border-blue-100'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <Icon className={`h-5 w-5 mr-3 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Reports section in mobile menu */}
              {filteredReportsItems.length > 0 && (
                <div className="pt-2">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Rapporter
                  </div>
                  {filteredReportsItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentView === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavigation(item.id)}
                        className={`w-full flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-blue-50 text-blue-700 shadow-sm border border-blue-100'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <Icon className={`h-5 w-5 mr-3 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </nav>
          </div>

          {/* Logout button */}
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-4 py-3 text-base font-medium rounded-lg text-red-700 bg-red-50 hover:bg-red-100 transition-colors duration-200 border border-red-200"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Logga ut
            </button>
          </div>
        </div>
      </div>
    </>
  );
};